
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useMantineTheme } from "@mantine/core";
import { ReactNode } from "react";
import BenchCard from "../../UIComponent/Components/BenchCard";

export interface BenchTableProps {
  children: ReactNode;
  striped?: boolean;
  hoverable?: boolean;
  compact?: boolean;
  bordered?: boolean;
  // shadow?: "xs" | "sm" | "md" | "lg" | "xl" | "none";
  className?: string;
  maxHeight?: number | string;
}

export function BenchTable({
  children,
  striped = true,
  hoverable = true,
  compact = false,
  bordered = false,
  // shadow = "sm",
  className,
  maxHeight,
}: BenchTableProps) {
  const theme = useMantineTheme();

  const tableStyles = css`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 12px;
    font-family: ${theme.fontFamily};
    background-color: transparent;

    thead {
      position: sticky;
      top: 0;
      z-index: 10;
      background-color: ${theme.white};
      tr th {
        border-bottom: 2px solid #ee374d; /* Persistent Red Accent Line under header */
      }
    }

    th {
      color: #ee374d;
      font-size: 11px;
      font-weight: 700;
      text-align: center;
      padding: 15px 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
      position: relative;
      
      &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 20%;
        height: 60%;
        width: 1.5px;
        background-color: #f1f3f5;
      }
    }

    tbody tr {
      background-color: ${theme.white};
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      
      td {
        padding: 16px 20px;
        font-size: 14px;
        font-weight: 500;
        color: #495057;
        text-align: center;
        border-top: 1px solid transparent;
        border-bottom: 1px solid transparent;

        &:first-of-type {
          border-left: 1px solid transparent;
          border-top-left-radius: 12px;
          border-bottom-left-radius: 12px;
          font-weight: 600;
          color: #212529;
        }
        
        &:last-of-type {
          border-right: 1px solid transparent;
          border-top-right-radius: 12px;
          border-bottom-right-radius: 12px;
        }
      }

      &:hover {
        // transform: translateY(-2px);
        // box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
        background-color: #FFF5F5 !important;
      }

      &.highlighted {
        background-color: #fff5f6 !important; /* Premium pinkish highlight */
      }
    }

    ${striped &&
    css`
      tbody tr:nth-of-type(even) {
        background-color: #F3F4F661;
      }
    `}

    ${hoverable &&
    css`
      tbody tr {
        cursor: pointer;
      }
    `}
  `;

  const containerStyles = css`
    background-color: transparent;
    padding: 0px;
    overflow-x: auto;
    overflow-y: auto;
    max-height: ${maxHeight ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight) : 'calc(100vh - 220px)'};

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }
    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  `;

  return (
    <BenchCard>
      <div css={containerStyles}>
        <table css={tableStyles} className={className}>
          {children}
        </table>
      </div>
    </BenchCard>
  );
}
